/**
 * Format dữ liệu form thành FormData cho WooCommerce
 * @param {Object} checkoutData - Dữ liệu checkout
 * @returns {FormData} FormData object
 */
export const formatCheckoutDataToFormData = (checkoutData) => {
  const formData = new FormData();
  
  // Thêm tất cả dữ liệu vào FormData
  Object.keys(checkoutData).forEach(key => {
    if (checkoutData[key] !== null && checkoutData[key] !== undefined) {
      if (Array.isArray(checkoutData[key])) {
        // Xử lý array data (cart items)
        checkoutData[key].forEach((item, index) => {
          if (typeof item === 'object') {
            Object.keys(item).forEach(itemKey => {
              formData.append(`${key}[${index}][${itemKey}]`, item[itemKey]);
            });
          } else {
            formData.append(`${key}[${index}]`, item);
          }
        });
      } else if (typeof checkoutData[key] === 'object') {
        // Xử lý object data
        Object.keys(checkoutData[key]).forEach(objKey => {
          formData.append(`${key}[${objKey}]`, checkoutData[key][objKey]);
        });
      } else {
        // Xử lý primitive data
        formData.append(key, checkoutData[key]);
      }
    }
  });
  
  return formData;
};

/**
 * Format dữ liệu thành URL encoded string
 * @param {Object} data - Dữ liệu cần format
 * @returns {string} URL encoded string
 */
export const formatToURLEncoded = (data) => {
  const params = new URLSearchParams();
  
  Object.keys(data).forEach(key => {
    if (data[key] !== null && data[key] !== undefined) {
      if (Array.isArray(data[key])) {
        data[key].forEach((item, index) => {
          if (typeof item === 'object') {
            Object.keys(item).forEach(itemKey => {
              params.append(`${key}[${index}][${itemKey}]`, item[itemKey]);
            });
          } else {
            params.append(`${key}[${index}]`, item);
          }
        });
      } else if (typeof data[key] === 'object') {
        Object.keys(data[key]).forEach(objKey => {
          params.append(`${key}[${objKey}]`, data[key][objKey]);
        });
      } else {
        params.append(key, data[key]);
      }
    }
  });
  
  return params.toString();
};

/**
 * Lấy country code từ tên quốc gia
 * @param {string} countryName - Tên quốc gia
 * @returns {string} Country code
 */
export const getCountryCode = (countryName = "Vietnam") => {
  const countryMap = {
    "Vietnam": "VN",
    "United States": "US",
    "United Kingdom": "GB",
    "Canada": "CA",
    "Australia": "AU",
    "Japan": "JP",
    "South Korea": "KR",
    "Singapore": "SG",
    "Thailand": "TH",
    "Malaysia": "MY",
    "Indonesia": "ID",
    "Philippines": "PH"
  };
  
  return countryMap[countryName] || "VN";
};

/**
 * Format giá tiền
 * @param {number|string} price - Giá tiền
 * @param {string} currency - Đơn vị tiền tệ
 * @returns {string} Formatted price
 */
export const formatPrice = (price, currency = "VND") => {
  const numPrice = typeof price === 'string' ? parseFloat(price) : price;
  
  if (currency === "VND") {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(numPrice);
  }
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(numPrice);
};

/**
 * Validate email format
 * @param {string} email - Email address
 * @returns {boolean} Is valid email
 */
export const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate phone number format (Vietnamese)
 * @param {string} phone - Phone number
 * @returns {boolean} Is valid phone
 */
export const isValidVietnamesePhone = (phone) => {
  // Vietnamese phone number regex: 0xxxxxxxxx (10 digits) or +84xxxxxxxxx
  const phoneRegex = /^(\+84|0)(3[2-9]|5[689]|7[06-9]|8[1-689]|9[0-46-9])[0-9]{7}$/;
  return phoneRegex.test(phone.replace(/\s/g, ''));
};

/**
 * Generate order note từ form data
 * @param {Object} formData - Form data
 * @returns {string} Order note
 */
export const generateOrderNote = (formData) => {
  let note = "";
  
  if (formData.orderComments) {
    note += `Customer Note: ${formData.orderComments}\n`;
  }
  
  if (formData.deliveryMethod) {
    note += `Delivery Method: ${formData.deliveryMethod === 'shipment' ? 'By Shipment' : 'In Person'}\n`;
  }
  
  if (!formData.useMyInfo) {
    note += "Ship to different address: Yes\n";
  }
  
  return note.trim();
};

/**
 * Get payment method display name
 * @param {string} paymentMethod - Payment method code
 * @returns {string} Display name
 */
export const getPaymentMethodDisplayName = (paymentMethod) => {
  const paymentMethods = {
    "vnpay": "VNPay",
    "paypal": "PayPal", 
    "cod": "Cash on Delivery",
    "stripe": "Credit Card",
    "bank_transfer": "Bank Transfer"
  };
  
  return paymentMethods[paymentMethod] || paymentMethod;
};

/**
 * Calculate shipping cost based on location and weight
 * @param {Object} shippingData - Shipping information
 * @param {number} totalWeight - Total weight of items
 * @returns {number} Shipping cost
 */
export const calculateShippingCost = (shippingData, totalWeight = 0) => {
  // Base shipping cost
  let shippingCost = 5.0; // $5 base
  
  // Add cost based on weight
  if (totalWeight > 1) {
    shippingCost += (totalWeight - 1) * 2; // $2 per additional kg
  }
  
  // Add cost based on location (simplified example)
  if (shippingData.shippingState && shippingData.shippingState !== shippingData.city) {
    shippingCost += 3.0; // $3 for inter-state shipping
  }
  
  return parseFloat(shippingCost.toFixed(2));
};

/**
 * Generate unique order ID
 * @returns {string} Unique order ID
 */
export const generateOrderId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substr(2, 5);
  return `ORDER-${timestamp}-${random}`.toUpperCase();
};

/**
 * Parse VNPay response from URL
 * @returns {Object} VNPay response parameters
 */
export const parseVNPayResponse = () => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') return null;
  
  const urlParams = new URLSearchParams(window.location.search);
  const params = {};
  
  // VNPay response parameters
  const vnpayParams = [
    'vnp_Amount', 
    'vnp_BankCode', 
    'vnp_BankTranNo',
    'vnp_CardType',
    'vnp_OrderInfo',
    'vnp_PayDate',
    'vnp_ResponseCode',
    'vnp_TmnCode',
    'vnp_TransactionNo',
    'vnp_TransactionStatus',
    'vnp_TxnRef', // Order ID
    'vnp_SecureHash'
  ];
  
  // Extract VNPay params from URL
  vnpayParams.forEach(param => {
    if (urlParams.has(param)) {
      params[param] = urlParams.get(param);
    }
  });
  
  // Check if there are any VNPay parameters
  if (Object.keys(params).length === 0) return null;
  
  // Add success flag based on response code
  params.isSuccess = params.vnp_ResponseCode === '00';
  
  // Add order ID
  params.orderId = params.vnp_TxnRef;
  
  return params;
};
